'use client';

import { createFilterOptions } from '@mui/material';

import { AutocompleteBase } from '../autocompleteBase/AutocompleteBase';
import { TextField } from '../textField/TextField';

import type { TNameType } from '../../types/components/components.type';
import type { TAvailableValueTypeFreeSolo } from '../autocompleteBase/autocompleteBase.type';
import type { TPhoneFieldProps } from './phoneField.type';

export const PhoneField = <TName extends TNameType>({
  name,
  label,
  value,
  handleChange,
  handleBlur,
  helperText,
  disabled = false,
  className,
  errorMessage,
  availableValues,
  phoneProps,
}: TPhoneFieldProps<TName>) => {
  const avValues = availableValues ?? [];
  const {
    autoFocus = false,
    size = 'medium',
    color = 'primary',
    maxRows = 1,
    minRows = 1,
    variant = 'outlined',
    autoComplete,
    endAdornment,
    sx,
  } = phoneProps ?? {};

  const filter = createFilterOptions<TAvailableValueTypeFreeSolo>({
    stringify: (option) => option.label,
  });

  return (
    <TextField
      {...(autoComplete && { autoComplete })}
      label={label}
      name={name}
      disabled={disabled}
      errorMessage={errorMessage}
      helperText={helperText}
      value={value[1] as TName}
      handleChange={({ key, value: newValue }) => {
        handleChange({ key, value: [value[0] ?? '', newValue || ''] }, avValues);
      }}
      handleBlur={handleBlur}
      autoFocus={autoFocus}
      size={size}
      color={color}
      maxRows={maxRows}
      minRows={minRows}
      variant={variant}
      className={className}
      type="tel" // value is the same as PHONE_FIELD value src/configs/formFields/userFields.ts
      autoComplete="tel-national" // phone number without phone country code
      startAdornment={
        <AutocompleteBase
          name="prefix" // different name to prevent browser heuristic autofill as a phone field
          disabled={disabled}
          label="" // empty to prevent label from being displayed
          value={value[0] ? [value[0]] : []}
          size={size}
          color={color}
          availableValues={avValues}
          handleChange={({ value: newValue }) => {
            handleChange({ key: name, value: [newValue[0] ?? '', value[1] ?? ''] }, avValues);
          }}
          handleBlur={handleBlur}
          filterOptions={(options, params) => filter(options, params)}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }

            return option.value;
          }}
          sx={{
            width: 200,
            '& fieldset': {
              border: 'none',
            },
          }}
        />
      }
      endAdornment={endAdornment}
      sx={{
        '& .MuiInputBase-adornedStart': { pl: 0 },
        ...sx, // we allow to override "default" styles
      }}
    />
  );
};
